import { z } from 'zod';
import { AffixListSchema } from '../data/affix-ids.js';
import { TRADE_MAX_GOLD, TRADE_MAX_ITEMS } from '../rules/trade.js';
/**
 * Trading (ADR 0034). A client asks a player nearby, answers, puts stacks
 * of its own bag and gold on the table, confirms, or cancels. The server
 * keeps the trade, shows each side the other's offer as snapshots, and
 * applies the exchange itself in one transaction. Nothing a client sends
 * names what it receives.
 */
const CharacterIdSchema = z.string().min(1).max(64);
export const TradeRequestMessageSchema = z.object({ name: z.string().trim().min(1).max(32) });
/** Answer the request of `from` (a character id, as `trade_requested` gave it). */
export const TradeAnswerMessageSchema = z.object({ from: CharacterIdSchema, accept: z.boolean() });
/** One's whole offer, replacing the previous one; both confirmations lapse. */
export const TradeOfferMessageSchema = z.object({
    items: z
        .array(z.object({ itemId: z.uuid(), quantity: z.number().int().min(1).max(99) }))
        .max(TRADE_MAX_ITEMS),
    gold: z.number().int().min(0).max(TRADE_MAX_GOLD),
});
export const TradeConfirmMessageSchema = z.object({}).strict();
export const TradeCancelMessageSchema = z.object({}).strict();
/** A stack on the table, as the other side sees it: what it is, never the rest of the bag. */
export const TradeStackSchema = z.object({
    itemId: z.uuid(),
    templateId: z.string().min(1),
    quantity: z.number().int().min(1).max(99),
    upgradeLevel: z.number().int().min(0).max(10),
    /** The piece's affixes, so the other side sees what it is offered (ADR 0057). */
    affixes: AffixListSchema.optional(),
});
export const TradeSideSchema = z.object({
    items: z.array(TradeStackSchema).max(TRADE_MAX_ITEMS),
    gold: z.number().int().min(0),
    confirmed: z.boolean(),
});
/** To both players after every change; null when the trade is over. */
export const TradeStateSchema = z
    .object({
    partnerId: CharacterIdSchema,
    partnerName: z.string().min(1),
    mine: TradeSideSchema,
    theirs: TradeSideSchema,
})
    .nullable();
/** To the player asked: who asks, and how long the question stands. */
export const TradeRequestedSchema = z.object({
    from: CharacterIdSchema,
    name: z.string().min(1),
    expiresInMs: z.number().int().min(0),
});
export const TRADE_EVENT_KINDS = [
    /** To the one who asked: the request is on its way. */
    'requested',
    'declined',
    'expired',
    'cancelled',
    /** Both confirmed and the exchange went through. */
    'completed',
    /** The other walked out of reach, or left the zone, or fell. */
    'too-far',
    'left',
];
export const TradeEventSchema = z.object({
    kind: z.enum(TRADE_EVENT_KINDS),
    /** The other player. */
    name: z.string().min(1),
});
export const TRADE_REFUSALS = [
    'invalid',
    /** Nobody of that name is in this zone. */
    'not-found',
    'self',
    'too-far',
    /** The other player is already trading, or dead. */
    'busy',
    'dead',
    /** A request to that player is already waiting. */
    'pending',
    /** The request answered no longer stands. */
    'no-request',
    'not-trading',
    'unknown-item',
    'quantity',
    'bound',
    /** A shop purchase still within its cooldown (ADR 0044). */
    'cooldown',
    'missing-gold',
    /** One of the two bags cannot take what it would receive. */
    'bag-full',
    /** The server is about to restart: a trade begun now would be cut short. */
    'restarting',
];
export const TradeRejectedSchema = z.object({ reason: z.enum(TRADE_REFUSALS) });
//# sourceMappingURL=trade.js.map